import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard, useWriteForbiddenGuard } from '../api/guard'
import { writeErrorMessage } from '../api/problems'
import { ADMINS_QUERY_KEY, useAdminsQuery, useIsSuperAdmin, type PlatformAdmin } from '../api/tier'
import { useAdminState } from '../context/AdminContext'
import { ConfirmDialog } from '../components/ConfirmDialog'
import './screens.css'

// The 012 CHECK set. `support` is the backend default (least privilege).
const ROLES = ['super_admin', 'admin', 'support', 'analyst'] as const

type Pending =
  | { kind: 'add'; email: string; role: string }
  | { kind: 'role'; admin: PlatformAdmin; role: string }
  | { kind: 'active'; admin: PlatformAdmin; active: boolean }

function label(a: PlatformAdmin): string {
  return a.email ?? a.user_id
}

export function AdminsView() {
  const qc = useQueryClient()
  const { identity, refreshIdentity } = useAdminState()
  const query = useAdminsQuery()
  useForbiddenGuard(query.error)
  const { isSuperAdmin } = useIsSuperAdmin()

  const [email, setEmail] = useState('')
  const [newRole, setNewRole] = useState<string>('support')
  const [roleDraft, setRoleDraft] = useState<Record<string, string>>({})
  const [pending, setPending] = useState<Pending | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)

  const write = useMutation({
    mutationFn: (p: Pending) => {
      if (p.kind === 'add') {
        return apiFetch('/admin/admins', {
          method: 'POST',
          body: JSON.stringify({ email: p.email, role: p.role }),
        })
      }
      const body = p.kind === 'role' ? { role: p.role } : { active: p.active }
      return apiFetch(`/admin/admins/${encodeURIComponent(p.admin.user_id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
    },
    onSuccess: (_data, p) => {
      if (p.kind === 'add') {
        setEmail('')
        setOkMsg(`Added ${p.email} as ${p.role}.`)
      } else if (p.kind === 'role') {
        setOkMsg(`${label(p.admin)} is now ${p.role}.`)
      } else {
        setOkMsg(`${label(p.admin)} ${p.active ? 'activated' : 'deactivated'}.`)
      }
      qc.invalidateQueries({ queryKey: ADMINS_QUERY_KEY })
      // A super admin changing their OWN role/active state: re-read whoami so the
      // write controls re-gate now instead of after a reload.
      if (p.kind !== 'add' && p.admin.user_id === identity?.user_id) {
        void refreshIdentity()
      }
    },
    onError: () => setOkMsg(null),
  })
  useWriteForbiddenGuard(write.error)

  const admins = query.data?.admins ?? []

  function confirm() {
    if (!pending) return
    setOkMsg(null)
    write.mutate(pending)
    setPending(null)
  }

  return (
    <section className="scr" aria-labelledby="admins-heading">
      <div className="screen-head">
        <h2 id="admins-heading">Admins</h2>
      </div>

      {!isSuperAdmin && (
        <p className="scr-note">
          Read-only for your tier. Adding admins, changing roles and (de)activating require a
          super admin.
        </p>
      )}

      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Failed to load admins.
        </p>
      )}

      {write.isError && (
        <p className="screen-error" role="alert">
          {writeErrorMessage(write.error)}
        </p>
      )}
      {okMsg && <p className="scr-ok">{okMsg}</p>}

      {isSuperAdmin && (
        <div className="scr-panel">
          <h3>Add an admin</h3>
          <p className="scr-note">
            By email of an existing Symone user. New admins default to <code>support</code>.
          </p>
          <div className="scr-panel-actions">
            <input
              type="email"
              className="scr-input"
              aria-label="New admin email"
              placeholder="person@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <select
              className="scr-input scr-input--narrow"
              aria-label="New admin role"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="screen-btn"
              disabled={!email.trim() || write.isPending}
              onClick={() => setPending({ kind: 'add', email: email.trim(), role: newRole })}
            >
              Add admin
            </button>
          </div>
        </div>
      )}

      <table className="scr-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Role</th>
            <th>Active</th>
            <th>Created</th>
            <th>Last sign-in</th>
            {isSuperAdmin && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {admins.map((a) => {
            const draft = roleDraft[a.user_id] ?? a.role ?? 'support'
            const isMe = a.user_id === identity?.user_id
            return (
              <tr key={a.user_id} className={a.active ? undefined : 'scr-row--muted'}>
                <td>
                  {a.email ?? '—'}
                  {isMe && <span className="scr-muted"> (you)</span>}
                </td>
                <td>
                  <span className="scr-badge">{a.role ?? '—'}</span>
                </td>
                <td>{a.active ? 'Yes' : 'No'}</td>
                <td>{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
                <td>
                  {a.last_sign_in_at ? new Date(a.last_sign_in_at).toLocaleDateString() : '—'}
                </td>
                {isSuperAdmin && (
                  <td>
                    <span className="scr-actions">
                      <select
                        className="scr-input scr-input--narrow"
                        aria-label={`Role for ${label(a)}`}
                        value={draft}
                        onChange={(e) =>
                          setRoleDraft((d) => ({ ...d, [a.user_id]: e.target.value }))
                        }
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="screen-btn"
                        aria-label={`Change role for ${label(a)}`}
                        disabled={draft === a.role || write.isPending}
                        onClick={() => setPending({ kind: 'role', admin: a, role: draft })}
                      >
                        Change role
                      </button>
                      <button
                        type="button"
                        className="screen-btn"
                        aria-label={`${a.active ? 'Deactivate' : 'Activate'} ${label(a)}`}
                        disabled={write.isPending}
                        onClick={() => setPending({ kind: 'active', admin: a, active: !a.active })}
                      >
                        {a.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </span>
                  </td>
                )}
              </tr>
            )
          })}
          {!query.isLoading && admins.length === 0 && (
            <tr>
              <td colSpan={isSuperAdmin ? 6 : 5} className="ws-empty">
                No platform admins.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {pending && (
        <ConfirmDialog
          label="Confirm admin change"
          onCancel={() => setPending(null)}
          onConfirm={confirm}
        >
          {pending.kind === 'add' && (
            <p>
              Grant <strong>{pending.role}</strong> platform-admin access to{' '}
              <strong>{pending.email}</strong>?
            </p>
          )}
          {pending.kind === 'role' && (
            <p>
              Change <strong>{label(pending.admin)}</strong> from {pending.admin.role ?? '—'} to{' '}
              <strong>{pending.role}</strong>?
            </p>
          )}
          {pending.kind === 'active' && (
            <p>
              {pending.active ? 'Activate' : 'Deactivate'} <strong>{label(pending.admin)}</strong>?
              {!pending.active && ' They lose admin-panel access on their next request.'}
            </p>
          )}
        </ConfirmDialog>
      )}
    </section>
  )
}
